import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MoreHorizontalIcon, PlusIcon, ChevronRightIcon, BuildingIcon, UserIcon, LandmarkIcon, SmartphoneIcon, CreditCardIcon, WalletIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
import { mockSuppliers } from '../mocks/mockSuppliers';
export function SupplierDetail() {
  const {
    t
  } = useTranslation('suppliers');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const supplier = mockSuppliers.find(s => s.id === id) || mockSuppliers[0];
  const fullAddress = [supplier.addressLine1, supplier.addressLine2, supplier.city, supplier.province, supplier.postcode, supplier.country].filter(Boolean).join(', ');
  const billingEmail = supplier.useAsBillingEmail ? supplier.email : `${supplier.email} (separate billing)`;
  const bills = {
    total: supplier.amountOwed !== `${getTenant().currency.code} 0.00` ? 1 : 0,
    due: supplier.amountOwed !== `${getTenant().currency.code} 0.00` ? 1 : 0,
    awaitingApproval: 0,
    scheduled: 0,
    paid: 0
  };
  const history = [{
    id: 'h1',
    action: `${supplier.name} was created`,
    date: supplier.dateCreated.replace(',', ' at')
  }];
  return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-13775" data-mp="2f0hvxgv0if0o">
      {/* Header */}
      <div className="flex items-center" data-id="element-13776" data-mp="1rax2u555a1do">
        <button onClick={() => navigate('/suppliers')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-13777" data-mp="1mb2b5zdjp7rk">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-13778" data-mp="215yr6yrlizf7" />
          {t('pages.SupplierDetail.all_suppliers')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-13779" data-mp="3cmyroho3kkkb">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-13780" data-mp="37p6r6z9dzq4i">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-13781" data-mp="37qht0tj9unsz">
            {/* Top Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13782" data-mp="3jqeso8xz25oc">
              <div className="flex justify-between items-start" data-id="element-13783" data-mp="3jotzonrb2usl">
                <div data-id="element-13784" data-mp="3oqt3fyqyjtq2">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-13785" data-mp="3oqazsh1yud84">
                    {supplier.name}
                  </h1>
                  <div className="flex items-center text-sm" data-id="element-13786" data-mp="2w13zpdmv5fkr">
                    <span className="text-gray-500 mr-2" data-id="element-13787" data-mp="2w2nibavzs2pp">
                      {t('pages.SupplierDetail.you_owe_them')}
                    </span>
                    <span className="px-2.5 py-0.5 bg-[#E2231A] text-white text-xs font-semibold rounded-md shadow-sm" data-id="element-13788" data-mp="35zjq1ee2gkt7">
                      {supplier.amountOwed}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-3" data-id="element-13789" data-mp="2d9tb534m1ew1">
                  <button onClick={() => navigate(`/bill-form?supplierId=${supplier.id}`)} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13790" data-mp="2i9fpxh03736y">
                    {t('pages.SupplierDetail.add_bill')}
                  </button>
                  <button aria-label="More actions" className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-13791" data-mp="lf1zf8jg9dl4">
                    <MoreHorizontalIcon size={20} aria-hidden="true" data-id="element-13792" data-mp="boqlqu9yrq11" />
                  </button>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13793" data-mp="2ou2x9a99ldw">
              <div className="flex justify-between items-center mb-6" data-id="element-13794" data-mp="2m7lmhxkbrih">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13795" data-mp="3ttadzcp503ua">
                  {t('pages.SupplierDetail.details')}
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#9F140F] hover:text-[#8C6C00]" data-id="element-13796" data-mp="1srud3nfn9f2k">
                  {t('pages.SupplierDetail.edit')}
                </button>
              </div>

              <div className="space-y-4" data-id="element-13797" data-mp="1o15nyerfsu6l">
                {/* Type */}
                <div className="grid grid-cols-3 gap-4 border-b border-gray-50 pb-4" data-id="element-13798" data-mp="1a1w517bujlyx">
                  <div className="text-sm text-gray-500" data-id="element-13799" data-mp="1a3z22bbf2fd5">
                    {t('pages.BulkSupplierUpload.type')}
                  </div>
                  <div className="col-span-2" data-id="element-13800" data-mp="1048cmo1pqqjx">
                    {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-13801" data-mp="153ulymef7mng">
                        <BuildingIcon size={12} className="mr-1.5" data-id="element-13802" data-mp="2zgu7ew5hfb2n" />
                        {t('pages.SupplierDetail.company')}
                      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-13803" data-mp="nrecnnty105p">
                        <UserIcon size={12} className="mr-1.5" data-id="element-13804" data-mp="xk274bxgas19" />
                        {t('pages.SupplierDetail.individual')}
                      </span>}
                  </div>
                </div>

                {/* Company-specific fields */}
                {supplier.supplierType === 'company' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13805" data-mp="1gi4lzxwab8lc">
                      <div className="text-sm text-gray-500" data-id="element-13806" data-mp="1ghd4fqzivbe2">
                        {t('pages.SupplierDetail.company_name_local')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13807" data-mp="3i8pw3echbms6">
                        {supplier.companyNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13808" data-mp="1h7tcd3ktbidf">
                      <div className="text-sm text-gray-500" data-id="element-13809" data-mp="1h9mmwtyjqmqp">
                        {t('pages.SupplierDetail.company_name_english')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13810" data-mp="1q76rabrpgz5j">
                        {supplier.companyNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13811" data-mp="2052w6lu1uxv7">
                      <div className="text-sm text-gray-500" data-id="element-13812" data-mp="254z2wsu4t3eg">
                        {t('pages.SupplierDetail.tax_id_registration_no')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-13813" data-mp="29wqth3daf94l">
                        {supplier.taxId || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13814" data-mp="2iyglaab30a2w">
                      <div className="text-sm text-gray-500" data-id="element-13815" data-mp="2nux7gyyq0edc">
                        {t('pages.SupplierDetail.branch')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13816" data-mp="2xxkjslwvvrdd">
                        {supplier.branch === '00000' ? '00000 (Head Office)' : supplier.branch || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13817" data-mp="2k1ywcj6atfn2">
                      <div className="text-sm text-gray-500" data-id="element-13818" data-mp="3gp3jojvm2pb6">
                        {t('pages.SupplierDetail.vat_registered')}
                      </div>
                      <div className="col-span-2" data-id="element-13819" data-mp="370bw0nxl2r62">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${supplier.vatRegistered ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-13820" data-mp="36z0pwhgo17bs">
                          {supplier.vatRegistered ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    {supplier.contactPerson && <div className="grid grid-cols-3 gap-4" data-id="element-13821" data-mp="mzajahul9dj6">
                        <div className="text-sm text-gray-500" data-id="element-13822" data-mp="n0llid8dyd86">
                          {t('pages.SupplierDetail.contact_person')}
                        </div>
                        <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13823" data-mp="d8oab6hdnl9g">
                          {supplier.contactPerson}
                        </div>
                      </div>}
                  </>}

                {/* Individual-specific fields */}
                {supplier.supplierType === 'individual' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13824" data-mp="vzfnyk8edxtn">
                      <div className="text-sm text-gray-500" data-id="element-13825" data-mp="w21tkebqa398">
                        {t('pages.SupplierDetail.full_name_thai')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13826" data-mp="3gr92t43vpyhc">
                        {supplier.fullNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13827" data-mp="1tu9q0nhs6jk4">
                      <div className="text-sm text-gray-500" data-id="element-13828" data-mp="27t9b5l634gbj">
                        {t('pages.SupplierDetail.full_name_english')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13829" data-mp="23s5xn5biewdh">
                        {supplier.fullNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13830" data-mp="1b4a0jzi132g6">
                      <div className="text-sm text-gray-500" data-id="element-13831" data-mp="1ou57yt185grs">
                        {t('pages.SupplierDetail.id_type')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 capitalize" data-id="element-13832" data-mp="1k23u52oerhzn">
                        {supplier.idType === 'citizen_id' ? 'Citizen ID' : supplier.idType === 'passport' ? 'Passport' : '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13833" data-mp="2p9bk56f871sa">
                      <div className="text-sm text-gray-500" data-id="element-13834" data-mp="2l66it76vt658">
                        {t('pages.SupplierDetail.id_number')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-13835" data-mp="2g6tzuxj4sj7x">
                        {supplier.idNumber || '—'}
                      </div>
                    </div>
                    {supplier.individualTaxId && <div className="grid grid-cols-3 gap-4" data-id="element-13836" data-mp="21hxq8w1fey57">
                        <div className="text-sm text-gray-500" data-id="element-13837" data-mp="21gcy1d2l3rai">
                          {t('pages.BulkSupplierUpload.tax_id')}
                        </div>
                        <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-13838" data-mp="1sgptiiek65d8">
                          {supplier.individualTaxId}
                        </div>
                      </div>}
                  </>}

                {/* Shared fields */}
                <div className="grid grid-cols-3 gap-4 border-t border-gray-50 pt-4" data-id="element-13839" data-mp="7yf9yop5idb0">
                  <div className="text-sm text-gray-500" data-id="element-13840" data-mp="czuwkl6z8rvi">
                    {t('pages.BulkSupplierUpload.email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13841" data-mp="gyaqza5e12iy">
                    {supplier.email}
                  </div>
                </div>
                {supplier.phone && <div className="grid grid-cols-3 gap-4" data-id="element-13842" data-mp="3lcip5o4f17if">
                    <div className="text-sm text-gray-500" data-id="element-13843" data-mp="3pf5ixuknd2i9">
                      {t('pages.BulkSupplierUpload.phone')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13844" data-mp="3u8rnt0kkk23i">
                      {supplier.phone}
                    </div>
                  </div>}
                <div className="grid grid-cols-3 gap-4" data-id="element-13845" data-mp="31ojba4ek1k8s">
                  <div className="text-sm text-gray-500" data-id="element-13846" data-mp="8v3yxciq43wz">
                    {t('pages.SupplierDetail.billing_email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13847" data-mp="3uydwf6ylfnv">
                    {billingEmail}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-13848" data-mp="dnx4u3pxhian">
                  <div className="text-sm text-gray-500" data-id="element-13849" data-mp="3m2nx6iebn5fh">
                    {t('pages.SupplierDetail.address')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13850" data-mp="3h4mbdppigs0b">
                    {fullAddress || '—'}
                  </div>
                </div>
                {supplier.internalReference && <div className="grid grid-cols-3 gap-4" data-id="element-13851" data-mp="3r079hcrgl84s">
                    <div className="text-sm text-gray-500" data-id="element-13852" data-mp="1acli6mqp8hdq">
                      {t('pages.SupplierDetail.internal_reference')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13853" data-mp="16dm6mjagpmdb">
                      {supplier.internalReference}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Pay-in Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13854" data-mp="13yfo47c4a7rt">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-13855" data-mp="18vfteg1576hv">
                {t('pages.SupplierDetail.payin_method')}
              </h3>

              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-13856" data-mp="1wnwdsx8crakc">
                <div className="flex items-center" data-id="element-13857" data-mp="21jvad83u3yvu">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${supplier.payInMethod === 'acquiring' ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-13858" data-mp="1mwijr4z672yl">
                    {supplier.payInMethod === 'acquiring' ? <WalletIcon size={20} className="text-green-700" data-id="element-13859" data-mp="2kb4k9ihiopny" /> : <CreditCardIcon size={20} className="text-blue-700" data-id="element-13860" data-mp="wmt38z5j9z0m" />}
                  </div>
                  <div data-id="element-13861" data-mp="rngl2rplxg4d">
                    <div className="text-sm font-semibold text-gray-900" data-id="element-13862" data-mp="rmx4bebp0xrx">
                      {supplier.payInMethod === 'acquiring' ? 'Acquiring Volume' : 'Card Payment'}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-13863" data-mp="1fdav71ijkqkx">
                      {supplier.payInMethod === 'acquiring' ? 'Funded from merchant acquiring settlement balance' : 'Charged to linked card'}
                    </div>
                  </div>
                </div>

                {supplier.payInMethod === 'card' && supplier.cardNumber && <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-13864" data-mp="1y3sbg6m6ttpe">
                    <div data-id="element-13865" data-mp="1tdbwuj6ms21g">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-13866" data-mp="1tctur5xaetlm">
                        {t('pages.SupplierDetail.cardholder')}
                      </div>
                      <div className="text-sm font-medium text-gray-900" data-id="element-13867" data-mp="21o2fie51vtmh">
                        {supplier.cardholderName}
                      </div>
                    </div>
                    <div data-id="element-13868" data-mp="2al334ikapnov">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-13869" data-mp="2flhdw8da7bql">
                        {t('pages.SupplierDetail.card_number')}
                      </div>
                      <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-13870" data-mp="2kk2c628z3lce">
                        {supplier.cardNumber}
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            {/* Payout Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13871" data-mp="2ybgz2k6046oq">
              <div className="flex justify-between items-center mb-6" data-id="element-13872" data-mp="33blcg1bm4laq">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13873" data-mp="38a643ccstwnk">
                  {t('pages.SupplierDetail.payout_method')}
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#9F140F] hover:text-[#8C6C00]" data-id="element-13874" data-mp="2imafc4nj4rgj">
                  {t('pages.SupplierDetail.edit_payout_method')}
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-13875" data-mp="2nj99gn770yeq">
                {supplier.payoutMethod === 'bank_account' ? <>
                    <div className="flex items-center mb-4" data-id="element-13876" data-mp="2wcw1c28a5hin">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-4" data-id="element-13877" data-mp="3bb7y08vnkq9t">
                        <LandmarkIcon size={20} className="text-green-700" data-id="element-13878" data-mp="36cmwm64o00js" />
                      </div>
                      <div data-id="element-13879" data-mp="1gxosocvds3dy">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-13880" data-mp="1gwx960f25w41">
                          {t('pages.SupplierDetail.bank_account')}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-13881" data-mp="3nm8pdo991mde">
                          {t('pages.SupplierDetail.direct_transfer')}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-13882" data-mp="3duuiuwivrmar">
                      <div data-id="element-13883" data-mp="3iscrelszjzi8">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13884" data-mp="34ufyq0hnkd8u">
                          {t('pages.SupplierDetail.bank_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-13885" data-mp="38xcivvmuchew">
                          {supplier.bankName}
                        </div>
                      </div>
                      <div data-id="element-13886" data-mp="2zw4kzeo8lilc">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13887" data-mp="2zxfpxhtf4we1">
                          {t('pages.SupplierDetail.account_number')}
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-13888" data-mp="y2nh8m3891vv">
                          {'***' + supplier.accountNumber.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-13889" data-mp="3d1fy6q79oke0">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13890" data-mp="3d3abbicar87j">
                          {t('pages.SupplierDetail.account_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-13891" data-mp="33ald3p5rbqu5">
                          {supplier.accountName}
                        </div>
                      </div>
                    </div>
                  </> : <>
                    <div className="flex items-center mb-4" data-id="element-13892" data-mp="2pak1benyeyzw">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-4" data-id="element-13893" data-mp="2pd6h21l00kt7">
                        <SmartphoneIcon size={20} className="text-purple-700" data-id="element-13894" data-mp="njp027aza7ls" />
                      </div>
                      <div data-id="element-13895" data-mp="ikcgpwjbfiny">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-13896" data-mp="im737fa8jiu5">
                          {t('pages.SupplierDetail.promptpay')}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-13897" data-mp="v2y9g0jhi6au">
                          {supplier.promptPayType === 'phone' ? 'Phone number' : supplier.promptPayType === 'citizen_id' ? 'Citizen ID' : 'Tax ID'}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-13898" data-mp="c3u6n9ijd8rm">
                      <div data-id="element-13899" data-mp="h2nhsvaconom">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13900" data-mp="h2f79a1p9ugl">
                          {t('pages.SupplierDetail.promptpay_id')}
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-13901" data-mp="1shu1w76fsa8j">
                          {'***' + supplier.promptPayId.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-13902" data-mp="1xhefjqueafvv">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13903" data-mp="1xg3dpwkifi7e">
                          {t('pages.SupplierDetail.display_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-13904" data-mp="26bj586xsll9t">
                          {supplier.promptPayName}
                        </div>
                      </div>
                    </div>
                  </>}
              </div>
            </div>

            {/* Source Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13905" data-mp="1ilf7uhb76hr9">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-13906" data-mp="1njgqj14pof27">
                {t('pages.SupplierDetail.source')}
              </h3>
              <div className="space-y-4" data-id="element-13907" data-mp="1sgqgqizxcur3">
                <div className="grid grid-cols-3 gap-4" data-id="element-13908" data-mp="2yzyhilpjhphg">
                  <div className="text-sm text-gray-500" data-id="element-13909" data-mp="2z1jcudjohcgd">
                    {t('pages.SupplierDetail.platform')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13910" data-mp="sb693lrab78e">
                    {supplier.source}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-13911" data-mp="il3g3lk5xta2">
                  <div className="text-sm text-gray-500" data-id="element-13912" data-mp="ijijzri7a2a3">
                    {t('pages.SupplierDetail.date_created')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13913" data-mp="9kf1opquxqwn">
                    {supplier.dateCreated}
                  </div>
                </div>
              </div>
            </div>

            {/* Bills Section */}
            <div className="p-8" data-id="element-13914" data-mp="3vsobves79o1y">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-13915" data-mp="3vtpp9pwoummu">
                {t('pages.SupplierDetail.bills')}
              </h3>
              <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors group" data-id="element-13916" data-mp="1tz750dbw8r89">
                <div data-id="element-13917" data-mp="3jhnmu622t41j">
                  <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-13918" data-mp="3jh426hl1k1jt">
                    {bills.total} {t('pages.SupplierDetail.bill')}
                    {bills.total !== 1 ? 's' : ''}{' '}
                    {t('pages.SupplierDetail.for')} {supplier.name}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-13919" data-mp="3ehrj87xajemi">
                    {bills.due} {t('pages.SupplierDetail.due')}{' '}
                    {bills.awaitingApproval}{' '}
                    {t('pages.SupplierDetail.awaiting_approval')}{' '}
                    {bills.scheduled} {t('pages.SupplierDetail.scheduled_and')}{' '}
                    {bills.paid} {t('pages.SupplierDetail.paid')}
                  </div>
                </div>
                <ChevronRightIcon size={20} className="text-gray-400 group-hover:text-gray-600 transition-colors" data-id="element-13920" data-mp="11l51yywbgy8" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - History */}
        <div className="lg:col-span-1" data-id="element-13921" data-mp="2e92xnlldvgzy">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-13922" data-mp="2ec77ayhmb4y0">
            {t('pages.SupplierDetail.history')}
          </h3>
          <div className="space-y-6" data-id="element-13923" data-mp="2o78xzyrjkf2p">
            {history.map((event, index) => <div key={event.id} className="relative flex gap-4" data-id="element-13924" data-mp="2j8nzc3rxu3gd" data-mpi={index}>
                {index !== history.length - 1 && <div className="absolute left-[9px] top-6 bottom-[-24px] w-px bg-gray-200" data-id="element-13925" data-mp="1vko3wqjolovg"></div>}
                <div className="relative z-10 flex-shrink-0 w-5 h-5 bg-gray-100 rounded flex items-center justify-center mt-0.5" data-id="element-13926" data-mp="1qm36szzq7lb8">
                  <PlusIcon size={12} className="text-gray-600" data-id="element-13927" data-mp="25b8zs67z0fao" />
                </div>
                <div data-id="element-13928" data-mp="20gmqc8lg8wt8">
                  <p className="text-sm font-medium text-gray-900" data-id="element-13929" data-mp="1csve14zwsjry">
                    {event.action}
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-13930" data-mp="17utuyk2hgkgh">{event.date}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}